import {
  Dialog as BaseDialog,
  DialogProps as BaseDialogProps,
  DialogActions,
  DialogTitle,
  IconButton,
  IconButtonProps,
} from '@mui/material';
import React, { ReactElement, ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui-compat';
import { CloseIcon } from '../../assets/icons';

const useStyles = makeStyles()((theme) => ({
  closeButton: {
    position: 'absolute',
    right: '12px',
    top: '12px',
    color: theme.palette.grey[500],
  },
}));

export interface DialogProps extends Omit<BaseDialogProps, 'title' | 'onClose'> {
  title?: ReactElement | string;
  actions?: ReactNode;
  onClose?: (
    event: React.SyntheticEvent<Element, Event>,
    reason: 'backdropClick' | 'escapeKeyDown' | 'cancelClick',
  ) => void;
}

export const Dialog: React.FC<DialogProps> = ({
  children,
  actions,
  title,
  onClose,
  ...props
}): ReactElement<DialogProps> => {
  const { classes } = useStyles();
  const [t] = useTranslation();
  const id = useId();

  return (
    <BaseDialog
      aria-labelledby={id}
      onClose={onClose}
      {...props}
      open={!!props.open}
      onClick={(e) => e.stopPropagation()}
    >
      {title && (
        <header>
          <DialogTitle className="mb-3 h-auto" id={id} onClick={(e) => e.stopPropagation()}>
            {title}
          </DialogTitle>

          {onClose && (
            <IconButton
              aria-label={t('SHARED_CANCEL')}
              className={classes.closeButton}
              onClick={
                onClose &&
                (((event: React.SyntheticEvent<Element, Event>) =>
                  onClose(event, 'cancelClick')) as IconButtonProps['onClick'])
              }
            >
              <CloseIcon className="w-5" />
            </IconButton>
          )}
        </header>
      )}
      {children}
      {actions && (
        <footer>
          <DialogActions>{actions}</DialogActions>
        </footer>
      )}
    </BaseDialog>
  );
};
